import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateRight } from "@fortawesome/free-solid-svg-icons";
import { ACTION_TYPES } from "../../../app/actionTypes";
import {
  setActionPayload,
  setActionType,
} from "../../../features/authorizationSlice";
import { formatAccount } from "../../../features/ValueFormater";
import AppButton from "../Common/Button";
import AmountInput from "../Common/AmountInput";
import FileUpload from "../Common/FileUpload";
import FormInput from "../Common/FormInput";
import "../../Styles/repeat-transaction.scss";

function readFileAsBase64(file, onLoad) {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onloadend = () => {
    onLoad(reader.result);
  };
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function DetailRow({ label, children }) {
  return (
    <div className="new-repeat-transaction__detail-row">
      <span className="new-repeat-transaction__detail-label">{label}</span>
      <div className="new-repeat-transaction__detail-value">{children}</div>
    </div>
  );
}

export default function RepeatTransaction({
  transactionID,
  modalOpen,
  setModalOpen,
  initialData,
  onOpenQrAfterGenerate,
}) {
  const dispatch = useDispatch();
  const { transactionDetails } = useSelector(
    (state) => state.transactionDetails.response,
  );

  const [selectedFile, setSelectedFile] = useState(null);
  const [filePayload, setFilePayload] = useState({});
  const [error, setError] = useState("");
  const [amountDraft, setAmountDraft] = useState("");
  const [reasonDraft, setReasonDraft] = useState("");

  useEffect(() => {
    setSelectedFile(null);
    setFilePayload({});
    setError("");
  }, [transactionID]);

  useEffect(() => {
    const reason =
      transactionDetails?.ReasonForTransfer ??
      initialData?.reason ??
      "";
    const amountValue = transactionDetails?.Amount ?? initialData?.amount ?? "";
    setAmountDraft(String(amountValue || ""));
    setReasonDraft(reason);
  }, [initialData?.amount, initialData?.reason, transactionDetails]);

  useEffect(() => {
    if (!selectedFile) {
      setFilePayload({});
      return;
    }

    const fileSizeLimit = 2 * 1024 * 1024;
    if (selectedFile.size > fileSizeLimit) {
      setError("File should not be larger than 2MB.");
      return;
    }

    setError("");
    readFileAsBase64(selectedFile, (base64String) => {
      setFilePayload({
        ReasonFileName: selectedFile.name,
        ReasonFile: base64String,
      });
    });
  }, [selectedFile]);

  useEffect(() => {
    const proofUrl = initialData?.proofOfPaymentUrl;
    if (!modalOpen || !proofUrl || selectedFile) return;

    let isCancelled = false;

    const hydrateProofFile = async () => {
      try {
        const response = await fetch(proofUrl);
        if (!response.ok) return;
        const blob = await response.blob();
        if (!blob || isCancelled) return;

        const fallbackName =
          initialData?.proofOfPayment && initialData.proofOfPayment !== "-"
            ? initialData.proofOfPayment
            : "proof-of-payment";
        const extension =
          blob.type === "application/pdf"
            ? ".pdf"
            : blob.type.includes("png")
              ? ".png"
              : blob.type.includes("jpeg") || blob.type.includes("jpg")
                ? ".jpg"
                : "";
        const fileName = fallbackName.includes(".")
          ? fallbackName
          : `${fallbackName}${extension}`;
        const proofFile = new File([blob], fileName, {
          type: blob.type || "application/octet-stream",
        });
        const base64String = await blobToBase64(blob);
        if (isCancelled) return;

        setSelectedFile(proofFile);
        setFilePayload({
          ReasonFileName: proofFile.name,
          ReasonFile: base64String,
        });
      } catch {
        // Keep popup usable even if existing proof file cannot be fetched.
      }
    };

    void hydrateProofFile();

    return () => {
      isCancelled = true;
    };
  }, [
    initialData?.proofOfPayment,
    initialData?.proofOfPaymentUrl,
    modalOpen,
    selectedFile,
  ]);

  useEffect(() => {
    if (!modalOpen) {
      setAmountDraft(String(transactionDetails?.Amount ?? initialData?.amount ?? ""));
      setReasonDraft(transactionDetails?.ReasonForTransfer ?? initialData?.reason ?? "");
    }
  }, [initialData?.amount, initialData?.reason, modalOpen, transactionDetails]);

  const amountDisplay = useMemo(() => {
    if (!amountDraft) return "";
    return formatAccount(
      amountDraft,
      transactionDetails?.CurrencyID ||
        (initialData?.currency === "USD" ? 1 : 2),
    );
  }, [amountDraft, initialData?.currency, transactionDetails?.CurrencyID]);

  const beneficiaryName =
    transactionDetails?.BeneficiaryName ||
    transactionDetails?.RecipientName ||
    initialData?.beneficiaryName ||
    "-";
  const sourceValue =
    transactionDetails?.IBAN ||
    transactionDetails?.RecipientMobileNo ||
    initialData?.fromAccount ||
    "-";

  const handleConfirm = () => {
    if (error) return;

    const parsedAmount = parseFloat(
      String(amountDraft).replace(/[^\d.,-]/g, "").replace(",", "."),
    );
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) return;
    const transferContext = String(initialData?.transferContext || "").toUpperCase();
    const normalizedReason = String(reasonDraft ?? "").trim();
    const resolvedPhoneNo =
      transactionDetails?.RecipientMobileNo ||
      transactionDetails?.PhoneNo ||
      initialData?.phoneNo ||
      (transferContext === "SUYOOL" ? initialData?.fromAccount : null) ||
      null;

    if (transferContext === "SUYOOL" && !resolvedPhoneNo) {
      setError("Recipient phone number is required for repeat transfer.");
      return;
    }

    if (!normalizedReason) {
      setError("Reason of transfer is required.");
      return;
    }

    const payload = {
      CurrencyID: transactionDetails?.CurrencyID,
      BeneficiaryName: beneficiaryName,
      IBAN: transactionDetails?.IBAN ?? null,
      PhoneNo: resolvedPhoneNo,
      Amount: String(parsedAmount),
      ReasonOfTransfer: normalizedReason,
      ReasonFileName: filePayload?.ReasonFileName,
      ReasonFile: filePayload?.ReasonFile,
    };

    setModalOpen(false);

    if (transferContext === "BSL" || transactionDetails?.IBAN) {
      dispatch(setActionType(ACTION_TYPES.PUSH_BANK_TRANSFER));
      dispatch(setActionPayload(payload));
      onOpenQrAfterGenerate?.();
      return;
    }

    if (transferContext === "SUYOOL" || transactionDetails?.RecipientMobileNo) {
      dispatch(setActionType(ACTION_TYPES.PUSH_S2S_TRANSFER));
      dispatch(setActionPayload(payload));
      onOpenQrAfterGenerate?.();
    }
  };

  return (
    <Modal
      centered
      show={modalOpen}
      onHide={() => setModalOpen(false)}
      dialogClassName="new-repeat-transaction-modal"
    >
      <Modal.Body className="new-repeat-transaction">
        <div className="new-repeat-transaction__header">
          <div className="new-repeat-transaction__badge">
            <FontAwesomeIcon icon={faRotateRight} />
          </div>
          <div className="new-repeat-transaction__header-copy">
            <h3>Repeat Transfer</h3>
            <p>Reuse the previous transfer details and update only what changed.</p>
          </div>
        </div>

        <div className="new-repeat-transaction__amount-card">
          <AmountInput
            label="Transfer Amount"
            prefix={
              (transactionDetails?.CurrencyID ||
                (initialData?.currency === "USD" ? 1 : 2)) === 1
                ? "$"
                : "L.L"
            }
            value={amountDraft}
            placeholder={amountDisplay || "Enter amount"}
            onChange={(event) => setAmountDraft(event.target.value)}
            className="new-repeat-transaction__amount-input"
          />
        </div>

        <div className="new-repeat-transaction__details">
          <DetailRow label="From Account">
            <div className="new-repeat-transaction__readonly-value">
              {sourceValue}
            </div>
          </DetailRow>
          <DetailRow label="Beneficiary Name">
            <div className="new-repeat-transaction__readonly-value">
              {beneficiaryName}
            </div>
          </DetailRow>
          <DetailRow label="Reason">
            <FormInput
              value={reasonDraft}
              placeholder="Enter reason"
              onChange={(event) => setReasonDraft(event.target.value)}
              className="new-repeat-transaction__reason-input"
            />
          </DetailRow>
          <DetailRow label="Upload File">
            <FileUpload
              label=""
              title="Choose a file"
              description="Optional supporting document up to 2MB"
              maxFileSize={2 * 1024 * 1024}
              selectedFile={selectedFile}
              error={error}
              onValidationError={setError}
              onFileChange={setSelectedFile}
            />
          </DetailRow>
        </div>

        <div className="new-repeat-transaction__actions">
          <AppButton
            type="button"
            variant="outline"
            className="new-repeat-transaction__secondary"
            onClick={() => setModalOpen(false)}
          >
            Not Now
          </AppButton>
          <AppButton
            type="button"
            className="new-repeat-transaction__primary"
            onClick={handleConfirm}
            disabled={Boolean(error)}
          >
            Repeat Payment
          </AppButton>
        </div>
      </Modal.Body>
    </Modal>
  );
}
